import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { filterBusinessPriorities } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/businessPriorities/helpers'
import {
  EXTENDED_BUSINESS_UNITS_BLANK,
  EXTENDED_BUSINESS_UNITS_PRINT,
  EXTENDED_BUSINESS_UNITS_PS,
} from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditorModal/configs'
import { useJbpSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { getJBPActualsAndTargetsCategoriesCrud } from 'legacy/lut/services/partner360/common/helpers/getJBPActualsAndTargetsCategoriesCrud'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { filterKeyInitiativeSettingsByYear } from 'partner360/features/keyInitiativeEditor/lib/filterKeyInitiativeSettingsByYear'
import { transformKeyObjectivesDataToHierarchyFormat } from 'partner360/features/keyInitiativeEditor/lib/transformKeyObjectivesDataToHierarchyFormat'
import { useKeyInitiativePartnerMarket } from 'partner360/features/keyInitiativeEditor/model/hooks/useKeyInitiativePartnerMarket'

interface IUseCopyValidationDictionariesParams {
  programType: ProgramTypes
  fYear: number
  partnerId: string
}

export const useCopyValidationDictionaries = ({
  programType,
  fYear,
  partnerId,
}: IUseCopyValidationDictionariesParams) => {
  const {
    jbpSettings,
    isLoaded: isJbpSettingsLoaded,
    isLoading: isJbpSettingsLoading,
  } = useJbpSettings()
  const partnerMarket = useKeyInitiativePartnerMarket(partnerId, fYear)
  const availableBusinessPriorities = useMemo(
    () =>
      filterBusinessPriorities({
        priorities: jbpSettings.kiBusinessPriorities,
        fYear,
        programType,
        market: partnerMarket,
      }),
    [jbpSettings.kiBusinessPriorities, fYear, programType, partnerMarket]
  )
  const [businessUnits, setBusinessUnits] = useState<string[]>([])
  const [isBusinessUnitsLoading, setIsBusinessUnitsLoading] = useState(true)
  const [hasBusinessUnitsError, setHasBusinessUnitsError] = useState(false)

  const loadBusinessUnits = useCallback(async () => {
    setIsBusinessUnitsLoading(true)
    setHasBusinessUnitsError(false)

    try {
      const response = await getJBPActualsAndTargetsCategoriesCrud({ programType, fYear }).fetch()
      setBusinessUnits(response.items.map((item) => item.Category).filter(Boolean))
    } catch {
      setBusinessUnits([])
      setHasBusinessUnitsError(true)
    } finally {
      setIsBusinessUnitsLoading(false)
    }
  }, [fYear, programType])

  useEffect(() => {
    void loadBusinessUnits()
  }, [loadBusinessUnits])

  const keyObjectivesForYear = useMemo(
    () => filterKeyInitiativeSettingsByYear(jbpSettings.keyObjectives, fYear),
    [fYear, jbpSettings.keyObjectives]
  )

  const strategicInitiatives = useMemo(
    () =>
      transformKeyObjectivesDataToHierarchyFormat({
        keyObjectivesSettingsList: keyObjectivesForYear,
        programType,
      }).map((item) => item.key),
    [keyObjectivesForYear, programType]
  )

  const allowedBusinessUnits = useMemo(() => {
    const extendedBusinessUnits =
      programType === ProgramTypes.PC || programType === ProgramTypes.PersonalSystems
        ? [...EXTENDED_BUSINESS_UNITS_PS, ...EXTENDED_BUSINESS_UNITS_BLANK]
        : programType === ProgramTypes.CorePrint || programType === ProgramTypes.Print
          ? [...EXTENDED_BUSINESS_UNITS_PRINT, ...EXTENDED_BUSINESS_UNITS_BLANK]
          : []

    return [...businessUnits, ...extendedBusinessUnits.map((item) => item.Category)]
  }, [businessUnits, programType])

  const strategicInitiativeSet = useMemo(
    () => new Set(strategicInitiatives),
    [strategicInitiatives]
  )
  const businessUnitSet = useMemo(() => new Set(allowedBusinessUnits), [allowedBusinessUnits])

  return {
    keyObjectivesForYear,
    availableBusinessPriorities,
    strategicInitiatives: strategicInitiativeSet,
    businessUnits: businessUnitSet,
    isLoading: isJbpSettingsLoading || !isJbpSettingsLoaded || isBusinessUnitsLoading,
    hasError: hasBusinessUnitsError,
    retry: loadBusinessUnits,
  }
}
